<script setup lang="ts" generic="TItem">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  itemCount: number
  windowSize?: number
  navigationEnabled?: boolean
  touchAction?: string
}>()

const emit = defineEmits<{
  (e: 'reachEnd'): void
  (e: 'indexChange', index: number): void
}>()

const currentIndex = ref(0)
const dragOffset = ref(0)
const isAnimating = ref(false)
const isNavigationLocked = ref(false)
const feedEl = ref<HTMLElement | null>(null)
const feedHeight = ref(1)
const animMs = 260

const windowSize = computed(() => props.windowSize ?? 2)
const before = computed(() => windowSize.value)
const after = computed(() => windowSize.value)
const touchAction = computed(() => props.touchAction ?? 'pan-y')
const navigationEnabled = computed(() => props.navigationEnabled !== false)

const renderedIndices = computed(() => {
  if (props.itemCount <= 0) return []

  const start = Math.max(0, currentIndex.value - before.value)
  const end = Math.min(props.itemCount - 1, currentIndex.value + after.value)
  const indices: number[] = []

  for (let index = start; index <= end; index++) {
    indices.push(index)
  }

  if (end + 1 < props.itemCount) {
    indices.push(end + 1)
  }

  return indices
})

const trackStyle = computed(() => {
  const baseY = currentIndex.value * 100
  const safeFeedHeight = feedHeight.value || 1
  const offsetPercent = (dragOffset.value / safeFeedHeight) * 100
  const transform = `translateY(-${baseY + offsetPercent}%)`
  const transition = isAnimating.value ? `transform ${animMs}ms ease` : 'none'
  return { transform, transition }
})

let startY = 0
let startTime = 0
let isDragging = false
let wheelLock = false
const isTouchNavigating = ref(false)

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const updateFeedHeight = () => {
  const currentHeight = feedEl.value?.clientHeight || window.innerHeight || 1
  feedHeight.value = Math.max(1, currentHeight)
}

const resetDragState = () => {
  isDragging = false
  isTouchNavigating.value = false
  dragOffset.value = 0
}

const interruptNavigation = () => {
  resetDragState()
  isAnimating.value = false
  isNavigationLocked.value = false
}

const resetNavigationState = () => {
  interruptNavigation()
}

watch(currentIndex, value => {
  resetDragState()
  emit('indexChange', value)
  if (value >= props.itemCount - 2) {
    emit('reachEnd')
  }
})

watch(
  () => props.itemCount,
  nextCount => {
    if (nextCount <= 0) {
      currentIndex.value = 0
      interruptNavigation()
      return
    }

    if (currentIndex.value > nextCount - 1) {
      currentIndex.value = nextCount - 1
    }
  },
)

watch(navigationEnabled, enabled => {
  if (!enabled) {
    interruptNavigation()
  }
})

const onWheel = (event: WheelEvent) => {
  if (!navigationEnabled.value || props.itemCount <= 0) return

  event.preventDefault()

  if (wheelLock || isNavigationLocked.value) return

  wheelLock = true
  isAnimating.value = true
  isNavigationLocked.value = true

  const direction = event.deltaY > 0 ? 1 : -1
  const targetIndex = clamp(currentIndex.value + direction, 0, props.itemCount - 1)

  if (targetIndex !== currentIndex.value) {
    currentIndex.value = targetIndex
    dragOffset.value = 0
  }

  window.setTimeout(() => {
    isAnimating.value = false
    isNavigationLocked.value = false
    wheelLock = false
  }, animMs + 50)
}

const onTouchStart = (event: TouchEvent) => {
  if (!navigationEnabled.value || isNavigationLocked.value || !event.touches || event.touches.length !== 1) return

  startY = event.touches[0]!.clientY
  startTime = Date.now()
  isDragging = true
  isTouchNavigating.value = true
  isAnimating.value = false
}

const onTouchMove = (event: TouchEvent) => {
  if (!navigationEnabled.value || !isTouchNavigating.value || !isDragging) return
  if (!event.touches || event.touches.length !== 1) return

  event.preventDefault()
  const currentY = event.touches[0]!.clientY
  const delta = currentY - startY
  dragOffset.value = -delta
}

const onTouchEnd = () => {
  if (!navigationEnabled.value || !isTouchNavigating.value || props.itemCount <= 0) return

  isDragging = false
  isTouchNavigating.value = false
  isAnimating.value = true
  isNavigationLocked.value = true

  const duration = Date.now() - startTime
  const distance = Math.abs(dragOffset.value)
  const vh = feedHeight.value || 1
  const distanceRatio = distance / vh

  let threshold = 0.5
  if (duration < 150) {
    threshold = 0.02
  } else if (duration < 300) {
    threshold = 0.2
  } else if (duration < 500) {
    threshold = 0.35
  }

  const offsetRatio = dragOffset.value / vh
  let targetIndex = currentIndex.value

  if (distanceRatio > threshold) {
    if (offsetRatio > 0) {
      targetIndex = Math.min(currentIndex.value + 1, props.itemCount - 1)
    } else if (offsetRatio < 0) {
      targetIndex = Math.max(currentIndex.value - 1, 0)
    }
  }

  currentIndex.value = targetIndex
  dragOffset.value = 0

  window.setTimeout(() => {
    isAnimating.value = false
    isNavigationLocked.value = false
  }, animMs)
}

const onTouchCancel = () => {
  interruptNavigation()
}

const handleVisibility = () => {
  if (document.visibilityState !== 'visible') {
    interruptNavigation()
  }
}

function jumpToIndex(index: number) {
  if (index < 0 || index >= props.itemCount) {
    console.warn('索引超出范围:', index)
    return
  }

  interruptNavigation()
  currentIndex.value = index
}

onMounted(() => {
  updateFeedHeight()
  document.addEventListener('visibilitychange', handleVisibility)
  window.addEventListener('resize', updateFeedHeight)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibility)
  window.removeEventListener('resize', updateFeedHeight)
})

defineExpose({
  jumpToIndex,
  currentIndex: computed(() => currentIndex.value),
  resetDragState,
  interruptNavigation,
  resetNavigationState,
})
</script>

<template>
  <div
    ref="feedEl"
    class="h-full w-full overflow-hidden bg-gray-950 flex"
    :style="{ touchAction }"
    @wheel="onWheel"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchCancel"
  >
    <div class="flex-1" :style="trackStyle">
      <slot :rendered-indices="renderedIndices" :current-index="currentIndex" />
    </div>
  </div>
</template>
